import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';

@Component({
  selector: 'orderform-widget-styler',
  templateUrl: './widget-styler.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetStylerComponent implements OnInit {
  @Input()
  widgetId: string;

  @Input()
  elements: string;

  @Input()
  fontSize?: string = '1';

  @Input()
  textColor?: string = 'rgba(4, 37, 62, 1)';

  @Input()
  inputFieldColor?: string = 'rgba(255,255,255,1.0)';

  @Input()
  borderColor?: string = 'rgba(180, 190, 197, 1)';

  @Input()
  borderWidth?: string = '1';

  @Input()
  borderRadius?: string = '2';

  @Input()
  borderShadow?: string = 'ocb-shadow-select-default';

  styles?: string;

  constructor() {}

  ngOnInit(): void {
    if (!this.widgetId) {
      throw new Error('No widget id provided');
    }
    if (!this.elements) {
      throw new Error('No elements list provided');
    }

    this.generateStyles();
  }

  isUsingElement(element: string) {
    return this.elements
      .split(',')
      .find((e) => e.toUpperCase() === element.toUpperCase());
  }

  generateStyles() {
    this.styles = `<style type="text/css">
      #${this.widgetId} {
        font-size: ${this.fontSize}rem;
      }

      ${
        this.isUsingElement('legend')
          ? this.generateLegendStyles()
          : '/* no legend styles */'
      }

      ${
        this.isUsingElement('input')
          ? this.generateInputStyles()
          : '/* no input styles */'
      }

      ${
        this.isUsingElement('checkbox')
          ? this.generateCheckboxStyles()
          : '/* no checkbox styles */'
      }

      ${
        this.isUsingElement('radio')
          ? this.generateRadioStyles()
          : '/* no radio styles */'
      }

      ${
        this.isUsingElement('dropdown')
          ? this.generateDropdownStyles()
          : '/* no dropdown styles */'
      }

      ${
        this.isUsingElement('textarea')
          ? this.generateTextareaStyles()
          : '/* no textarea styles */'
      }

      ${
        this.isUsingElement('calendar')
          ? this.generateCalendarStyles()
          : '/* no calendar styles */'
      }
    </style>`;
  }

  generateInputStyles() {
    return `
      /** Input **/
      #${this.widgetId} .input-container input {
        color: ${this.textColor};
        background-color: ${this.inputFieldColor};
        /* Split border settings to improve overwriting in error cases */
        border-color: ${this.borderColor};
        border-width: ${this.borderWidth}px !important;
        border-radius: ${this.borderRadius}px;
        font-size: 100%;

        ${this.generateBorderShadow()}
      }

      #${this.widgetId} .input-container input::placeholder {
        color: ${this.borderColor};
      }

      #${this.widgetId} .input-container input[type='text'],
      #${this.widgetId} .input-container input[type='email'] {
        margin-top: calc(1em + 6px);
      }

      #${this.widgetId} .input-container .transition-label {
        color: ${this.borderColor} !important;
        font-size: 100% !important;
        left: calc(${this.borderWidth}px + 12px);
        top: calc(${this.borderWidth}px + 15px + 1em);
      }

      #${this.widgetId} .input-container label,
      #${this.widgetId} .input-container input ~ .transition-label.not-move,
      #${this.widgetId} .input-container input:focus ~ .transition-label {
        color: ${this.textColor} !important;
        font-size: 75% !important;
      }

      #${this.widgetId} .input-container input ~ .transition-label.not-move,
      #${this.widgetId} .input-container input:focus ~ .transition-label {
        transform: translate3d(
          calc(-12px - ${this.borderWidth}px),
          calc(-${this.borderWidth}px - 15px - 1em)
        , 0);
      }
    `;
  }

  generateDropdownStyles() {
    return `
      /** Dropdown **/
      #${this.widgetId} .input-container p-dropdown {
        font-size: 100%;
        color: ${this.textColor};
        background-color: ${this.inputFieldColor};
        /* Split border settings to improve overwriting in error cases */
        border-color: ${this.borderColor};
        border-width: ${this.borderWidth}px !important;
        border-radius: ${this.borderRadius}px;
      }

      #${this.widgetId} .input-container p-dropdown,
      #${
        this.widgetId
      } .input-container p-dropdown .p-dropdown .p-dropdown-panel {
        ${this.generateBorderShadow()}
      }

      #${
        this.widgetId
      } .input-container p-dropdown .p-dropdown .p-dropdown-trigger-icon {
        color: ${this.textColor};
        opacity: 0.3;
      }

      #${
        this.widgetId
      } .input-container p-dropdown .p-dropdown .p-dropdown-panel,
      #${
        this.widgetId
      } .input-container p-dropdown .p-dropdown .p-dropdown-panel .p-dropdown-items-wrapper{
        border-radius: ${this.borderRadius}px;
      }

      #${this.widgetId} .input-container .p-dropdown-items {
        background-color: ${this.inputFieldColor};
      }

      #${
        this.widgetId
      } .input-container p-dropdown .p-dropdown-item.p-highlight,
      #${this.widgetId} .input-container p-dropdown .p-dropdown-item:hover{
        background-color: ${this.borderColor};
      }

      #${
        this.widgetId
      } .input-container p-dropdown .p-dropdown .p-dropdown-header .p-dropdown-filter-container .p-dropdown-filter {
        margin-top: 0;
        background-color: ${this.inputFieldColor};
      }

      #${
        this.widgetId
      } .input-container p-dropdown .p-dropdown .p-dropdown-header .p-dropdown-filter-container .p-dropdown-filter::placeholder {
        color: ${this.borderColor};
      }

      #${
        this.widgetId
      } .input-container p-dropdown .p-dropdown .p-dropdown-header .p-dropdown-filter-container .p-dropdown-filter-icon {
        background-color: ${this.borderColor};
      }
    `;
  }

  generateCheckboxStyles() {
    return `
      /** Checkbox **/
      #${this.widgetId} input[type=checkbox] + .input-checkbox {
        padding-left: 1.875em;
      }

      #${this.widgetId} input[type=checkbox] + .input-checkbox:before {
        width: 1em;
        height: 1em;
        top: 0.35em;
        background-size: 80%;
      }


      #${this.widgetId} label.input-checkbox {
        font-size: 87.5% !important;
      }
    `;
  }

  generateRadioStyles() {
    return `
      /** Radio **/
      #${this.widgetId} input[type=radio] + .input-radio {
        padding-left: 1.875em;
      }

      #${this.widgetId} input[type=radio] + .input-radio:before {
        width: 1.25em;
        height: 1.25em;
        top: 0.2em;
      }

      #${this.widgetId} label.input-radio {
        font-size: 87.5% !important;
      }
    `;
  }

  generateLegendStyles() {
    return `
      /** Legend **/
      #${this.widgetId} fieldset > legend {
        font-size: 87.5%;
        color: ${this.textColor};
      }
    `;
  }

  generateTextareaStyles() {
    return `
      /** TextArea **/
      #${this.widgetId} .input-container textarea {
        color: ${this.textColor};
        background-color: ${this.inputFieldColor};
        border: ${this.borderWidth}px solid ${this.borderColor};
        border-radius: ${this.borderRadius}px;
        font-size: 100%;

        ${this.generateBorderShadow()}
      }
    `;
  }

  generateCalendarStyles() {
    return `
      /** Calendar Input **/
      #${this.widgetId} .input-container p-calendar input[type='text'] {
        margin-top: 0;
      }

      #${this.widgetId} .input-container p-calendar .p-datepicker {
        color: ${this.textColor};
        background-color: ${this.inputFieldColor};
        border: ${this.borderWidth}px solid ${this.borderColor};
        border-radius: ${this.borderRadius}px;
      }

      #${this.widgetId} .input-container p-calendar .p-datepicker-header {
        color: ${this.borderColor};
      }

      #${this.widgetId} .input-container p-calendar .p-datepicker-calendar thead th {
        color: ${this.borderColor};
        border-color: ${this.borderColor};
      }

      #${this.widgetId} .input-container p-calendar .p-datepicker .p-highlight,
      #${this.widgetId} .input-container p-calendar .p-datepicker td span:hover {
        color: ${this.textColor} !important;
        background-color: ${this.borderColor} !important;
      }

      #${this.widgetId} .input-container p-calendar .p-datepicker td.p-datepicker-today span {
        border-color: ${this.borderColor} !important;
      }

      #${this.widgetId} .input-container p-calendar .p-datepicker table tr th > *,
      #${this.widgetId} .input-container p-calendar .p-datepicker .p-datepicker-header button {
        opacity: 1;
      }
    `;
  }

  generateBorderShadow() {
    switch (this.borderShadow) {
      case 'ocb-shadow-select-default':
        return 'box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);';
      case 'ocb-shadow-select-medium':
        return 'box-shadow: 0 1px 5px rgba(0,0,0,0.4);';
      case 'ocb-shadow-select-strong':
        return 'box-shadow: 0 1px 5px rgba(0,0,0,0.7)';
      case 'ocb-shadow-select-blurred':
        return 'box-shadow: 0 0 25px rgba(0,0,0,0.2),0 0 15px rgba(0,0,0,0.2),0 0 3px rgba(0,0,0,0.4);';
      case 'ocb-shadow-select-none':
      default:
        return 'box-shadow: none;';
    }
  }
}
